import React, { useState, useEffect, useCallback } from 'react';

export default function AdminSponsorships({ token }) {
  const [sponsorshipRequests, setSponsorshipRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchSponsorshipData = useCallback(async () => {
    const authToken =
      token ||
      localStorage.getItem('adminToken') ||
      localStorage.getItem('token') ||
      '';

    if (!authToken) {
      setError('Missing admin token. Please log out and log back in.');
      setSponsorshipRequests([]);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch('/.netlify/functions/admin-get-sponsorships', {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = { error: text };
      }

      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      setSponsorshipRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch (err) {
      setError(err.message || 'Failed to load sponsorship requests');
      setSponsorshipRequests([]);
      console.error('Sponsorship fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const authToken =
      token ||
      localStorage.getItem('adminToken') ||
      localStorage.getItem('token') ||
      '';
    if (!authToken) return;
    fetchSponsorshipData();
  }, [token, fetchSponsorshipData]);

  const printValue = (v) => {
    const s = String(v ?? '').trim();
    return s ? s : 'Not provided';
  };

  const handlePrint = () => {
    window.print();
  };

  // Filter by source and search
  const filtered = sponsorshipRequests.filter((req) => {
    if (filterTab === 'applications' && req.source !== 'sponsorship_app') return false;
    if (filterTab === 'registrations' && req.source !== 'registration') return false;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const name = `${req.firstName || ''} ${req.lastName || ''}`.toLowerCase();
      const email = String(req.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    }
    return true;
  });

  const statsApplications = sponsorshipRequests.filter((r) => r.source === 'sponsorship_app').length;
  const statsRegistrations = sponsorshipRequests.filter((r) => r.source === 'registration').length;

  const renderSponsorshipTable = () => (
    <div style={{ overflowX: 'auto', marginTop: 12 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
            <th style={{ padding: '12px', fontWeight: 600 }}>Name</th>
            <th style={{ padding: '12px', fontWeight: 600 }}>Email</th>
            <th style={{ padding: '12px', fontWeight: 600 }}>Phone</th>
            <th style={{ padding: '12px', fontWeight: 600 }}>Scholarship Type</th>
            <th style={{ padding: '12px', fontWeight: 600 }}>Source</th>
            <th style={{ padding: '12px', fontWeight: 600 }}>Reason</th>
            <th style={{ padding: '12px', fontWeight: 600 }}>Submitted</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No sponsorship requests found
              </td>
            </tr>
          ) : (
            filtered.map((req, idx) => (
              <tr
                key={idx}
                style={{
                  borderBottom: '1px solid #eee',
                  background: idx % 2 === 0 ? '#fff' : '#f9f9f9'
                }}
              >
                <td style={{ padding: '12px' }}>
                  <strong>{printValue(`${req.firstName || ''} ${req.lastName || ''}`)}</strong>
                </td>
                <td style={{ padding: '12px' }}>{printValue(req.email)}</td>
                <td style={{ padding: '12px' }}>{printValue(req.phone)}</td>
                <td style={{ padding: '12px' }}>{printValue(req.scholarshipType)}</td>
                <td style={{ padding: '12px', fontSize: 12 }}>
                  {req.source === 'sponsorship_app' ? 'App' : 'Registration'}
                </td>
                <td style={{ padding: '12px', maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  <div style={{ fontSize: 13, color: '#555', lineHeight: 1.4 }}>
                    {req.sponsorshipReason?.substring(0, 80)}
                    {req.sponsorshipReason?.length > 80 ? '...' : ''}
                  </div>
                </td>
                <td style={{ padding: '12px', fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                  {req.submittedAt ? new Date(req.submittedAt).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ padding: '0 1rem' }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
        }
        .print-only { display: none; }
        .print-card {
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 12px;
          page-break-inside: avoid;
        }
        .print-grid {
          display: grid;
          grid-template-columns: 220px 1fr;
          gap: 6px 12px;
        }
        .print-label { font-weight: 700; }
      `}</style>

      <h2 style={{ marginTop: 0 }}>Sponsorship Requests</h2>

      {/* Screen-only controls */}
      <div className="no-print">
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, padding: 12, background: '#dbeafe', borderRadius: 8, border: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: 12, color: '#0c4a6e', marginBottom: 4 }}>Standalone Applications</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#0c4a6e' }}>{statsApplications}</div>
          </div>

          <div style={{ flex: 1, padding: 12, background: '#e9d5ff', borderRadius: 8, border: '1px solid #d8b4fe' }}>
            <div style={{ fontSize: 12, color: '#581c87', marginBottom: 4 }}>Registration Sponsorships</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#581c87' }}>{statsRegistrations}</div>
          </div>

          <div style={{ flex: 1, padding: 12, background: '#fecaca', borderRadius: 8, border: '1px solid #fca5a5' }}>
            <div style={{ fontSize: 12, color: '#7f1d1d', marginBottom: 4 }}>Total Requests</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#7f1d1d' }}>{sponsorshipRequests.length}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search by name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, flex: 1, minWidth: 200 }}
          />

          <button className="btn" onClick={fetchSponsorshipData} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>

          <button className="btn btn-primary" onClick={handlePrint} disabled={loading}>
            Print All Requests
          </button>
        </div>

        {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>Error: {error}</div>}
        {loading && <div style={{ color: '#666' }}>Loading sponsorship requests...</div>}
        {!loading && !error && renderSponsorshipTable()}
      </div>

      {/* Print-only full detail */}
      <div className="print-only">
        <h2>Sponsorship Requests (Full Detail)</h2>
        <p>Printed: {new Date().toLocaleString()}</p>
        <p>Total: {filtered.length}</p>

        {filtered.length === 0 ? (
          <p>No sponsorship requests found.</p>
        ) : (
          filtered.map((req, idx) => (
            <div key={`print-${idx}`} className="print-card">
              <h3 style={{ marginTop: 0 }}>Request #{idx + 1}</h3>
              <div className="print-grid">
                <div className="print-label">Source</div><div>{req.source === 'sponsorship_app' ? 'Standalone App' : 'Registration'}</div>
                <div className="print-label">Submitted</div><div>{req.submittedAt ? new Date(req.submittedAt).toLocaleString() : 'Not provided'}</div>
                <div className="print-label">First Name</div><div>{printValue(req.firstName)}</div>
                <div className="print-label">Last Name</div><div>{printValue(req.lastName)}</div>
                <div className="print-label">Email</div><div>{printValue(req.email)}</div>
                <div className="print-label">Phone</div><div>{printValue(req.phone)}</div>
                <div className="print-label">Scholarship Type</div><div>{printValue(req.scholarshipType)}</div>
                <div className="print-label">Partial Pay Amount</div><div>{printValue(req.partialPayAmount)}</div>
                <div className="print-label">Sponsorship Reason</div><div>{printValue(req.sponsorshipReason)}</div>
                <div className="print-label">Fundraising Efforts</div><div>{printValue(req.fundraisingEfforts)}</div>
                <div className="print-label">Fundraising Details</div><div>{printValue(req.fundraisingDetails)}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}